import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import ProtectedRoute from './ProtectedRoute';

const DashboardOverview = Loadable(lazy(() => import('pages/dashboard/default')));
const RolesPage = Loadable(lazy(() => import('pages/roles')));
const Goals = Loadable(lazy(() => import('pages/master/goal')));
const FoodTypePage = Loadable(lazy(() => import('pages/master/food-type')));
const BlogCategory = Loadable(lazy(() => import('pages/master/blog-category')));
const TierPage = Loadable(lazy(() => import('pages/master/tier')));
const DietaryPreference = Loadable(lazy(() => import('pages/master/dietary-preference')));
const ExerciseType = Loadable(lazy(() => import('pages/master/exercise-type')));
const CoachCategory = Loadable(lazy(() => import('pages/coach/coach-category')));
const Coaches = Loadable(lazy(() => import('pages/coach/coaches')));
const CmsPage = Loadable(lazy(() => import('pages/cms')));
const FaqPage = Loadable(lazy(() => import('pages/cms/faq')));
const BannerPage = Loadable(lazy(() => import('pages/cms/banner')));
const StoryPage = Loadable(lazy(() => import('pages/cms/story')));
const BlogPage = Loadable(lazy(() => import('pages/cms/blog')));
const BlogAddEdit = Loadable(lazy(() => import('pages/cms/blog/BlogAddEdit')));
const BlogDetail = Loadable(lazy(() => import('pages/cms/blog/BlogDetail')));
const UserReportPage = Loadable(lazy(() => import('pages/cms/user-report')));
const UserReportDetail = Loadable(lazy(() => import('pages/cms/user-report/UserReportDetail')));
const UserReportEdit = Loadable(lazy(() => import('pages/cms/user-report/UserReportEdit')));
const AboutUsPage = Loadable(lazy(() => import('pages/cms/about-us')));
const AboutUsAddEdit = Loadable(lazy(() => import('pages/cms/about-us/AboutUsAddEdit')));
const CoachDetails = Loadable(lazy(() => import('pages/coach/coaches/CoachDetails')));
const InstantConsultation = Loadable(lazy(() => import('pages/consultations/instantConsultation')));
const LongTermConsultation = Loadable(lazy(() => import('pages/consultations/longTermConsultation')));
const ConsultationDetails = Loadable(lazy(() => import('pages/consultations/instantConsultation/ConsultationDetails')));
const FollowUp = Loadable(lazy(() => import('pages/consultations/longTermConsultation/FollowUp')));
const UserList = Loadable(lazy(() => import('pages/users')));
const UserDetails = Loadable(lazy(() => import('pages/users/UserDetails')));
const SubscriptionPlan = Loadable(lazy(() => import('pages/plans/index')));
const PlanForm = Loadable(lazy(() => import('pages/plans/PlanForm')));
const PlanCategory = Loadable(lazy(() => import('pages/master/planCategory/index')));
const UploadFoodFile = Loadable(lazy(() => import('pages/master/uploadFood/UploadFoodFile')));
const UploadExerciseFile = Loadable(lazy(() => import('pages/master/uploadExercise/UploadExerciseFile')));
const PlanDetails = Loadable(lazy(() => import('pages/plans/PlanDetails')));
const GymList = Loadable(lazy(() => import('pages/gym')));
const GymForm = Loadable(lazy(() => import('pages/gym/GymForm')));
const GymVerticalBanners = Loadable(lazy(() => import('pages/gym/vertical-banner')));

const MainRoutes = {
  path: '/',
  element: (
    <ProtectedRoute>
      <DashboardLayout />
    </ProtectedRoute>
  ),
  children: [
    { index: true, element: <Navigate to="/dashboard" replace /> },
    {
      path: 'dashboard',
      children: [
        { index: true, element: <DashboardOverview /> },
        { path: '*', element: <Navigate to="/dashboard" replace /> }
      ]
    },
    { path: 'roles', element: <RolesPage /> },
    { path: 'roles/admins', element: <RolesPage /> },
    {
      path: 'gym/onboarding',
      children: [
        { index: true, element: <GymList /> },
        { path: 'create', element: <GymForm /> },
        { path: 'edit/:id', element: <GymForm /> }
      ]
    },
    { path: 'gym/vertical-banner', element: <GymVerticalBanners /> },
    {
      path: 'users',
      children: [
        { index: true, element: <UserList /> },
        { path: 'detail/:id', element: <UserDetails /> }
      ]
    },
    {
      path: 'master',
      children: [
        { path: 'food-type', element: <FoodTypePage /> },
        { path: 'blog-category', element: <BlogCategory /> },
        { path: 'plan-category', element: <PlanCategory /> },
        { path: 'tier', element: <TierPage /> },
        { path: 'dietary-preference', element: <DietaryPreference /> },
        { path: 'exercise-types', element: <ExerciseType /> },
        { path: 'goal', element: <Goals /> },
        { path: 'upload-food', element: <UploadFoodFile /> },
        { path: 'upload-excersice', element: <UploadExerciseFile /> }
      ]
    },
    {
      path: 'consultation',
      children: [
        { path: 'category', element: <CoachCategory /> },
        { path: 'instant-consultation', element: <InstantConsultation /> },
        { path: 'lognterm-consultation', element: <LongTermConsultation /> },
        { path: 'consultation-details/:id', element: <ConsultationDetails /> },
        { path: 'consultation-followup/:id', element: <FollowUp /> }
      ]
    },
    {
      path: 'coach',
      children: [
        { path: 'tier', element: <TierPage /> },
        { path: 'coaches', element: <Coaches /> },
        { path: 'detail/:id', element: <CoachDetails /> }
      ]
    },
    {
      path: 'subscription',
      children: [
        { path: 'plan', element: <SubscriptionPlan /> },
        { path: 'create-plan', element: <PlanForm /> },
        { path: 'edit-plan/:id', element: <PlanForm /> },
        { path: 'plan-details/:id', element: <PlanDetails /> }
      ]
    },
    {
      path: 'cms',
      children: [
        { path: 'faq', element: <FaqPage /> },
        { path: 'banner', element: <BannerPage /> },
        { path: 'story', element: <StoryPage /> },
        {
          path: 'about-us',
          children: [
            { index: true, element: <AboutUsPage /> },
            { path: 'create', element: <AboutUsAddEdit /> },
            { path: 'edit', element: <AboutUsAddEdit /> }
          ]
        },
        {
          path: 'blog',
          children: [
            { index: true, element: <BlogPage /> },
            { path: 'create', element: <BlogAddEdit /> },
            { path: 'edit/:id', element: <BlogAddEdit /> },
            { path: 'detail/:id', element: <BlogDetail /> }
          ]
        },
        { path: ':cmsType', element: <CmsPage /> },
        {
          path: 'user-report',
          children: [
            { index: true, element: <UserReportPage /> },
            { path: 'detail/:id', element: <UserReportDetail /> },
            { path: 'edit/:id', element: <UserReportEdit /> }
          ]
        }
      ]
    }
  ]
};

export default MainRoutes;
